import type { TranslationTree } from '../../types';

export const analytics: TranslationTree = {
  analytics: {
    dashboard: {
      title: 'Analytique',
      description:
        'Comparez les références de la flotte, mesurez leur poids dans la production et identifiez les contextes où elles performent le mieux.',
      breadcrumb: {
        analytics: 'Analyses avancées',
      },
      sections: {
        performanceSummary: "Vue d'ensemble de la performance",
        performanceSummaryDescription:
          'Score global et indicateurs clés de performance de la référence.',
        keyMetrics: 'Indicateurs clés',
        keyMetricsDescription: "Vue d'ensemble des performances de la référence sélectionnée.",
        performanceAnalysis: 'Analyse de performance',
        performanceAnalysisDescription:
          'Comparaison avec les autres références et configuration du processus.',
        environmentalImpact: 'Impact environnemental',
        environmentalImpactDescription:
          'Analyse comparative des performances par machine, matériau et opérateur.',
        detailedBreakdown: 'Analyse détaillée',
        detailedBreakdownDescription:
          'Distribution des cycles, utilisation des machines et matériaux.',
        portfolioTrends: 'Portfolio et tendances',
        portfolioTrendsDescription:
          "Vue d'ensemble du portfolio de références et évolution dans le temps.",
      },
      controls: {
        referenceLabel: 'Référence outil',
        lastUpdated: 'Dernière mise à jour : {{date}}',
        pieceCount: '{{count}} pièce(s) produites',
      },
      summary: {
        uniqueTools: 'Outils uniques',
        totalTraces: 'Traces totales',
        ranking: 'Classement',
        rankValue: '{{rank}} sur {{total}}',
        noRanking: 'Non classé',
        avgTracesPerTool: 'Traces moy. / outil',
      },
      keyIndicators: {
        title: 'Indicateurs clés',
        description: 'Vue rapide des métriques agrégées pour toutes les traces de cette référence.',
        noData: 'Aucune donnée disponible',
        noDataDescription:
          'Aucune trace trouvée pour cette référence. Sélectionnez une autre référence ou actualisez les données.',
        totalDuration: 'Durée totale',
        totalDurationDesc: 'Sur {{count}} traces',
        durationByPiece: 'Durée par pièce',
        durationByPieceDesc: 'Moyenne',
        numberOfPieces: 'Nombre de pièces',
        numberOfPiecesDesc: 'Produites',
        volumeRemoved: 'Volume retiré',
        volumeRemovedDesc: 'Matière usinée',
        distanceTraveled: 'Distance parcourue',
        distanceTraveledDesc: "Total d'usinage",
        malround: 'Malrond',
        malroundDesc: 'Moyenne',
      },
      globalPerformance: {
        title: 'Performance globale',
        description:
          "Distribution d'usage et comparaison des performances entre tous les outils de cette référence.",
        usageDistribution: "Distribution d'usage",
        performanceComparison: 'Comparaison des performances',
        toolComparison: '{{count}} outil(s) comparé(s)',
        dimensions: {
          material: 'Matériau',
          machine: 'Machine',
          lubrication: 'Lubrification',
          toolHolder: 'Porte-outil',
        },
        metrics: {
          duration: 'Durée totale',
          pieces: 'Nombre de pièces',
          volume: 'Volume retiré',
          distance: 'Distance parcourue',
          malround: 'Malrond',
        },
      },
      complexCharts: {
        legend: {
          more: 'etc…',
        },
        title: 'Graphiques avancés',
        description:
          'Créez des graphiques personnalisés avec filtres multi-dimensions pour analyser les données.',
        addChart: 'Ajouter un graphique',
        exportConfig: 'Exporter la configuration',
        importConfig: 'Importer la configuration',
        saveAsDefault: 'Enregistrer par défaut',
        clearDefault: 'Effacer par défaut',
        exportSuccess: 'Configuration exportée avec succès',
        importSuccess: 'Configuration importée avec succès',
        saveSuccess: 'Configuration enregistrée par défaut',
        clearSuccess: 'Configuration par défaut effacée',
        importError: "Échec de l'importation. Veuillez vérifier le format du fichier.",
        exportError: "Échec de l'exportation de la configuration.",
        saveError: "Échec de l'enregistrement de la configuration par défaut.",
        clearError: 'Échec de la suppression de la configuration par défaut.',
        example: 'Exemple',
        removeChart: 'Supprimer le graphique',
        editTitle: 'Modifier le titre',
        showingTraces: 'Affichage de {{filtered}} sur {{total}} traces',
        noData: 'Aucune donnée disponible',
        noDataForConfig: 'Aucune donnée disponible pour la configuration sélectionnée.',
        chartError: 'Erreur de rendu du graphique',
        chartErrorDescription:
          "Une erreur inattendue s'est produite lors du rendu de ce graphique.",
        chartErrorTryAgain: 'Réessayer',
        filters: {
          title: 'Filtres',
          mode: 'Mode',
          filterMode: 'Mode de filtrage :',
          modeLabel: 'Mode de filtrage',
          allTraces: 'TOUTES LES TRACES',
          anyTrace: "N'IMPORTE QUELLE TRACE",
          clearAll: 'Tout effacer',
          machines: 'Machines',
          materials: 'Matériaux',
          toolHolders: 'Portes-outils',
          lubrications: 'Lubrifications',
          selectMachines: 'Sélectionner des machines',
          selectMaterials: 'Sélectionner des matériaux',
          selectToolHolders: 'Sélectionner des portes-outils',
          selectLubrications: 'Sélectionner des lubrifications',
          activeFilters: 'Filtres actifs',
          noFilters: 'Aucun filtre appliqué',
          noMachines: 'Aucune machine',
          noMaterials: 'Aucun matériau',
          noToolHolders: 'Aucun porte-outil',
          noLubrications: 'Aucune lubrification',
          machineLabel: 'Machine :',
          materialLabel: 'Matériau :',
          holderLabel: 'Porte-outil :',
          lubricationLabel: 'Lubrification :',
          addFilter: 'Ajouter un filtre',
          allData: 'Toutes les données sont affichées',
          minMaxError: 'min > max',
          filterCount: '{{count}} actif(s)',
          sectionCategorical: 'Catégoriel',
          sectionNumeric: 'Plage numérique',
          sectionDate: 'Plage de dates',
          removeRule: 'Supprimer la règle',
          negateRule: 'Inverser la règle',
          negateTitle: 'Inverser la condition (≠)',
          valuePlaceholder: 'Valeur…',
          kindBadge: {
            categorical: 'CAT',
            numeric: 'NUM',
            date: 'DATE',
          },
          operators: {
            in: 'est',
            not_in: "n'est pas",
            range: 'entre',
            gte: '≥',
            lte: '≤',
            date_range: 'entre',
            after: 'après',
            before: 'avant',
          },
          dimensions: {
            machine: 'Machine',
            material: 'Matériau',
            lubrication: 'Lubrification',
            toolHolder: 'Porte-outil',
            totalMachiningDuration: 'Durée totale',
            pieceMachiningDuration: 'Durée par pièce',
            machinedVolume: 'Volume usiné',
            machinedDistance: 'Distance parcourue',
            machinedPieces: 'Pièces usinées',
            malround: 'Faux-rond',
            toolState: "État de l'outil",
            startedAt: 'Démarré le',
            endedAt: 'Terminé le',
            vc: 'Vitesse de coupe (vc)',
            fz: 'Avance par dent (fz)',
            ap: 'Profondeur axiale (ap)',
            ae: 'Profondeur radiale (ae)',
            operation: 'Opération',
            clampingType: 'Type de bridage',
            reference: 'Référence',
          },
        },
        tooltip: {
          tool: 'Outil',
          trace: 'Trace',
          traceIndex: 'Index de trace',
          category: 'Catégorie',
          value: 'Valeur',
          count: 'Nombre',
          frequency: 'Fréquence',
          range: 'Plage',
          aggregatedFrom: 'Agrégé de {{count}} traces',
          aggregatedFromTools: 'Agrégé de {{count}} outils',
          ofTotal: '{{percent}}% du total',
          ofData: '{{percent}}% des données',
          min: 'Min',
          max: 'Max',
          median: 'Médiane',
          mean: 'Moyenne',
          q1: 'Q1',
          q3: 'Q3',
          outliers: 'Valeurs aberrantes',
          samples: 'Échantillons',
          reference: 'Référence',
          toolCount: '{{count}} outil(s)',
          traceCount: '{{count}} trace(s)',
          vc: 'Vitesse de coupe (vc)',
          fz: 'Avance par dent (fz)',
          ap: 'Profondeur axiale (ap)',
          ae: 'Profondeur radiale (ae)',
          operation: 'Opération',
          clampingType: 'Type de serrage',
        },
        configuration: {
          title: 'Configuration',
          chartType: 'Type de graphique',
          xAxis: 'Axe X',
          yAxis: 'Axe Y',
          groupBy: 'Grouper par',
          aggregation: 'Agrégation',
          aggregationLevel: "Niveau d'agrégation",
          byTool: 'Par outil',
          byTrace: 'Par trace',
          byReference: 'Par référence',
          advancedOptions: 'Options avancées',
          binCount: 'Nombre de classes',
          bins: 'classes',
          bins5: '5 classes',
          bins10: '10 classes',
          bins20: '20 classes',
          bins50: '50 classes',
          showGridLines: 'Afficher la grille',
          showDataLabels: 'Afficher les étiquettes',
          showTrendLine: 'Afficher la tendance',
          colorBy: 'Couleur par',
          sortOrder: 'Ordre de tri',
          ascending: 'Croissant',
          descending: 'Décroissant',
          alphabetical: 'Alphabétique',
          radarMode: 'Mode radar',
          radarMaxItems: 'Éléments max',
          radarModes: {
            categories: 'Comparer les catégories',
            metrics: 'Comparer les métriques',
          },
          showOutliers: 'Afficher les valeurs aberrantes',
          sortAscending: 'Croissant',
          sortDescending: 'Décroissant',
          sortAlphabetical: 'Alphabétique',
          types: {
            scatter: 'Nuage de points',
            bar: 'Barres',
            histogram: 'Histogramme',
            pie: 'Camembert',
            radar: 'Radar',
            boxplot: 'Boîte à moustaches',
          },
          metrics: {
            totalMachiningDuration: 'Durée totale',
            pieceMachiningDuration: 'Durée par pièce',
            machinedPieces: 'Nombre de pièces',
            machinedVolume: 'Volume retiré',
            machinedDistance: 'Distance parcourue',
            durationByPiece: 'Durée par pièce',
            numberOfPieces: 'Nombre de pièces',
            volumeRemoved: 'Volume retiré',
            distanceTraveled: 'Distance parcourue',
            malround: 'Malrond',
            machine: 'Machine',
            material: 'Matériau',
            toolHolder: 'Porte-outil',
            lubrication: 'Lubrification',
            operator: 'Opérateur',
            toolId: 'ID Outil',
            traceId: 'ID Trace',
            reference: 'Référence',
            toolState: "État de l'outil",
            traceCount: 'Nombre de traces',
            vc: 'Vitesse de coupe (Vc)',
            fz: 'Avance par dent (fz)',
            ap: 'Profondeur de coupe axiale (ap)',
            ae: 'Engagement radial (ae)',
            operation: 'Opération',
            clampingType: 'Type de bridage',
          },
          noData: 'Aucune donnée disponible pour la configuration sélectionnée',
          aggregations: {
            sum: 'Somme',
            mean: 'Moyenne',
            avg: 'Moyenne',
            median: 'Médiane',
            min: 'Minimum',
            max: 'Maximum',
            count: 'Comptage',
            p90: '90e Percentile',
            p95: '95e Percentile',
          },
        },
        defaults: {
          xAxisLabel: 'Axe X',
          yAxisLabel: 'Axe Y',
          categoryLabel: 'Catégorie',
          valueLabel: 'Valeur',
          traceIndexLabel: 'Index de trace',
          frequency: 'Fréquence',
          frequencyLabel: 'Fréquence (Nombre)',
          unknown: 'Inconnu',
        },
        axis: {
          traceIndex: 'Index de trace',
          traceNumber: 'Trace {{number}}',
        },
        widget: {
          viewOnly: 'Lecture seule',
        },
        export: {
          button: 'Exporter en PNG',
          success: 'Graphique exporté avec succès',
          error: "Erreur lors de l'export",
        },
        stale: {
          code: 'WARN_STALE_METRIC',
          title: 'Le graphique nécessite une reconfiguration',
          editConfig: 'Modifier la configuration',
          dismiss: 'Ignorer',
        },
        emptyState: {
          filterCode: 'ERR_FILTER_NO_MATCH',
          filterTitle: 'Aucune trace correspondante',
          filterBody: 'Les filtres actifs ont retourné 0 résultat. Essayez de les assouplir.',
          noDataCode: 'AWAIT_DATA',
          noDataTitle: 'Aucune donnée pour cette période',
          noDataBody:
            'Aucune trace disponible pour la référence et la plage de dates sélectionnées.',
          resetFilters: 'Réinitialiser les filtres',
          editConfig: 'Modifier la configuration',
          configure: 'Configurer le graphique',
        },
        removeDialog: {
          title: 'Supprimer ce graphique ?',
          subtitle: 'Destructif · Annulation disponible (5s)',
          body: '{{title}} sera supprimé de ce tableau de bord.',
          confirm: 'Supprimer le graphique',
          cancel: 'Annuler',
        },
        clearDefaultDialog: {
          title: 'Effacer la configuration par défaut ?',
          subtitle: 'Destructif · Impossible à annuler',
          body: 'Votre disposition de tableau de bord sauvegardée sera supprimée.',
          confirm: 'Effacer le défaut',
          cancel: 'Annuler',
        },
        undoRemove: {
          removed: '{{title}} supprimé',
          undo: 'Annuler',
          dismiss: 'Ignorer',
        },
        dragHandle: 'Glisser pour réorganiser',
        newChart: 'Nouveau graphique',
        chartNumber: 'Graphique {{number}}',
        layoutLabel: 'Taille du graphique',
        layout: {
          sm: 'Petit',
          md: 'Moyen',
          lg: 'Grand',
          full: 'Pleine largeur',
        },
      },
      shortcuts: {
        refresh: 'Appuyez sur R pour actualiser',
      },
      actions: {
        refresh: 'Actualiser les données',
        refreshing: 'Actualisation…',
        compareReferences: 'Comparaison de références',
      },
      filters: {
        title: 'Filtres',
        clearAll: 'Réinitialiser les filtres',
        machine: {
          label: 'Machine',
          placeholder: 'Sélectionner une machine',
          all: 'Toutes les machines',
        },
        lubrication: {
          label: 'Lubrification',
          placeholder: 'Sélectionner une lubrification',
          all: 'Toutes les lubrifications',
        },
        material: {
          label: 'Matériau',
          placeholder: 'Sélectionner un matériau',
          all: 'Tous les matériaux',
        },
      },
      common: {
        minutes: '{{value}} min',
        noData: 'Aucune donnée disponible',
        notSpecified: 'Non spécifié',
      },
      samples: {
        fallback: 'Aucun échantillon disponible',
        traces: '{{count}} relevés analysés',
        tracesFiltered: '{{filtered}} relevés analysés ({{total}} au total)',
        occurrences: '{{count}} occurrences',
        cycles: '{{count}} cycles',
        trend: '{{count}} périodes',
        references: '{{count}} références comparées',
      },
      error: {
        loadFailure:
          'Impossible de charger les données analytiques. Affichage de valeurs simulées.',
        title: 'Impossible de récupérer les données analytiques',
        detailsTitle: 'Détails techniques',
        detailName: 'Type : {{value}}',
        detailCode: 'Code : {{value}}',
        detailMessage: 'Message : {{value}}',
        detailTimestamp: 'Horodatage : {{value}}',
        detailsFallback: 'Aucun détail supplémentaire fourni.',
        retry: 'Réessayer',
        noRef: 'Aucune référence disponible. Vérifiez que des outils ont bien été enregistrés.',
        unknown: 'Une erreur inconnue est survenue.',
      },
      stats: {
        totalPieces: {
          title: 'Pièces produites',
          helper: 'Sur {{count}} relevés.',
          tooltip:
            "Nombre total de pièces usinées avec cette référence d'outil selon les relevés enregistrés.",
        },
        cycleEfficiency: {
          title: 'Efficacité des cycles',
          helper: "Usinage des pièces vs durée totale d'usinage",
          tooltip:
            "Rapport entre le temps d'usinage réel des pièces et la durée totale du cycle, exprimé en pourcentage. Un taux élevé indique une meilleure utilisation du temps machine.",
        },
        averageCycle: {
          title: 'Durée moyenne de cycle',
          helper: "Durée moyenne d'usinage par relevé",
          tooltip:
            "Temps moyen nécessaire pour compléter un cycle d'usinage avec cet outil. Permet d'estimer les temps de production.",
        },
        malround: {
          title: 'Malrond moyen',
          helper: "Indicateur d'état de surface (plus bas = mieux)",
          tooltip:
            "Mesure de la circularité de la pièce usinée. Plus la valeur est basse, meilleure est la qualité de l'état de surface.",
        },
        downtime: {
          title: "Taux d'arrêts",
          helper: 'Incidents pour dix relevés récents',
          value: '{{value}} / 10 relevés',
          tooltip:
            "Nombre d'incidents ou changements d'outil survenus pour dix relevés. Un taux élevé peut indiquer un besoin de maintenance ou des problèmes de procédé.",
        },
        operatorCoverage: {
          title: 'Couverture opérateurs',
          empty: '—',
          valueSingular: '{{count}} opérateur',
          valuePlural: '{{count}} opérateurs',
          helperEmpty: 'Aucun opérateur dominant enregistré.',
          helperWithShare: 'Opérateur principal : {{operator}} ({{share}}%)',
          helperWithoutShare: 'Opérateur principal : {{operator}}',
          tooltip:
            "Nombre d'opérateurs différents ayant utilisé cette référence et identification de l'opérateur principal. Permet de suivre la polyvalence de l'équipe.",
        },
        totalVolume: {
          title: 'Volume usiné total',
          helper: 'Volume cumulé sur tous les relevés',
          value: '{{value}} cm³',
          tooltip:
            "Volume total de matière enlevée avec cette référence d'outil. Indicateur clé de l'usure et de la charge de l'outil.",
        },
        averageVolume: {
          title: 'Volume moyen par relevé',
          helper: "Volume moyen par cycle d'usinage",
          value: '{{value}} cm³',
          tooltip:
            "Volume moyen de matière enlevée par relevé. Permet d'estimer la charge par cycle.",
        },
        totalDistance: {
          title: 'Distance parcourue totale',
          helper: 'Distance cumulée sur tous les relevés',
          value: '{{value}} mm',
          tooltip:
            "Distance totale parcourue par l'outil durant l'usinage. Facteur important pour anticiper l'usure de l'outil.",
        },
        averageDistance: {
          title: 'Distance moyenne par relevé',
          helper: "Distance moyenne par cycle d'usinage",
          value: '{{value}} mm',
          tooltip:
            "Distance moyenne parcourue par l'outil par relevé. Permet d'estimer la sollicitation par cycle.",
        },
      },
      comparison: {
        title: 'Comparaison des références',
        description: 'Comment cette référence se positionne par rapport au reste du portefeuille.',
        currentBadge: 'Actuelle',
        rank: 'Classée n°{{rank}} sur {{total}} références suivies.',
        rankUnavailable: 'Classement de la référence indisponible.',
      },
      process: {
        title: 'Configuration du procédé',
        description: 'Éléments de configuration les plus fréquents relevés récemment.',
        lubrication: 'Lubrification',
        holder: 'Porte-outil',
        clamping: 'Serrage',
        notProvided: 'Non renseigné',
        brandVariety: '{{count}} marque(s)',
        throughputShare: 'Part de production',
        throughputDetail: '{{share}} des pièces totales',
      },
      cycles: {
        title: 'Répartition des durées de cycle',
        description:
          "Comprenez la dispersion des cycles d'usinage pour repérer les anomalies et planifier la maintenance.",
        emptyLoading: 'Chargement des données de cycle…',
        xLabel: 'Durée de cycle [min]',
        yLabel: "Nombre de cycles d'usinage",
        emptyData: 'Aucune durée disponible pour cette référence.',
      },
      machines: {
        title: 'Répartition machines',
        description: 'Répartition des machines les plus associées à cette référence.',
        noActivity: 'Aucune activité machine enregistrée pour cette référence.',
        topInfo: '{{machine}} : {{share}} % des relevés récents.',
        emptyDistribution: 'Aucune répartition machine disponible.',
        empty: 'Aucune donnée machine disponible.',
      },
      operators: {
        title: 'Répartition opérateurs',
        description: 'Opérateurs intervenant le plus fréquemment sur cette référence.',
        listEmpty: 'Aucune donnée opérateur enregistrée pour cette référence.',
        empty: 'Aucune donnée opérateur disponible.',
      },
      materials: {
        title: 'Répartition matières',
        description: 'Matières les plus usinées avec la référence sélectionnée.',
        noInfo: 'Aucune information matière disponible pour cette référence.',
        infoSingular: '{{count}} matière usinée récemment.',
        infoPlural: '{{count}} matières usinées récemment.',
        emptyDistribution: 'Aucune répartition matière disponible.',
        empty: 'Aucune donnée matière disponible.',
      },
      changeReasons: {
        title: "Motifs d'intervention",
        description:
          "Motifs les plus fréquents enregistrés lors des changements d'état de l'outil.",
        empty: 'Aucun motif enregistré.',
        listEmpty: "Aucun motif de changement enregistré pour l'instant.",
        downtimeRate: "Taux d'arrêt : {{rate}}",
      },
      portfolio: {
        title: 'Portefeuille de références',
        description: "Part de production par référence au sein de l'organisation.",
      },
      empty: {
        noComparison: 'Aucune donnée de comparaison disponible.',
        selectReference: 'Sélectionnez une référence pour afficher les analyses.',
      },
      trend: {
        title: 'Tendance de production',
        description: 'Répartition des minutes productives et des arrêts enregistrés récemment.',
        producedSeries: 'Temps productif (min)',
        downtimeSeries: 'Temps hors production (min)',
        empty: 'Pas assez de données pour afficher la tendance.',
      },
      export: {
        downloadPNG: 'Télécharger en PNG',
        downloadCSV: 'Exporter les données (CSV)',
        share: 'Partager',
        shareText: 'Découvrez ce graphique :',
      },
      insights: {
        title: 'Observations et recommandations',
        description: 'Points clés identifiés automatiquement à partir de vos données.',
        highEfficiency: 'Excellente efficacité cycle de {{efficiency}}% - Très bonne performance !',
        lowEfficiency:
          "Efficacité cycle de {{efficiency}}% - Marge d'amélioration possible sur la qualité ou la configuration.",
        topPerformer: 'Cette référence est n°1 en volume de production - Référence vedette !',
        goodRank: 'Cette référence est dans le top {{rank}} - Bonne performance relative.',
        highDowntime:
          "{{downtime}} incidents pour 10 relevés - Taux d'arrêt élevé, vérifier les causes récurrentes.",
        noDowntime: 'Aucun incident détecté - Processus stable et fiable.',
        improvingTrend: 'Production en hausse de {{change}}% récemment - Tendance positive !',
        decliningTrend: 'Production en baisse de {{change}}% récemment - Tendance à surveiller.',
        highOperatorCoverage:
          '{{count}} opérateurs différents utilisent cette référence - Large adoption.',
        singleOperator:
          "Utilisé uniquement par {{operator}} - Envisager la formation d'autres opérateurs.",
        singleMachine: 'Utilisé uniquement sur {{machine}} - Limité à une seule machine.',
        lowDataVolume:
          'Peu de données disponibles - Augmenter le nombre de relevés pour des analyses plus fiables.',
        priority: {
          critical: 'Critique',
          attention: 'Attention',
          optimization: 'Optimisation',
        },
        category: {
          performance: 'Performance',
          trend: 'Tendance',
          anomaly: 'Anomalie',
          tip: 'Conseil',
        },
      },
      performanceSummary: {
        globalScore: 'Score global',
        classification: {
          excellent: 'Excellent',
          good: 'Bon',
          average: 'Moyen',
          'needs-improvement': 'À améliorer',
        },
        efficiency: 'Efficacité',
        quality: 'Qualité',
        reliability: 'Fiabilité',
        aboveAverage: 'Au-dessus de la moyenne',
        belowAverage: 'En dessous de la moyenne',
        excellentQuality: 'Qualité excellente',
        improvableQuality: 'Qualité améliorable',
        downtimePer10: 'incidents par 10 traces',
        ranking: 'Classement',
        productionShare: 'Part de production',
      },
      environmentalImpact: {
        machineAnalysis: 'Analyse par machine',
        materialAnalysis: 'Analyse par matériau',
        operatorAnalysis: 'Analyse par opérateur',
        malround: 'Malrond',
        efficiency: 'Efficacité',
        incidents: 'Incidents',
        traces: 'traces',
        pieces: 'pièces',
        noData: 'Aucune donnée disponible',
        optimalConfig: 'Configuration optimale identifiée',
        optimalConfigDescription:
          'Combinaison machine-matériau offrant les meilleures performances observées.',
        machine: 'Machine',
        material: 'Matériau',
      },
      subtitle: 'Analyse de performance par référence outil',
      managepanels: 'Gérer les panneaux',
      classicView: 'Vue classique',
      refresh: 'Actualiser',
      loading: 'Chargement des outils…',
      ref: {
        label: 'Référence',
        avgPieces: 'pcs moy.',
        tools: 'outils',
        states: {
          new: 'neuf',
          active: 'actif',
          worn: 'usé',
          broken: 'cassé',
          broken_early: 'cassé précoce',
          unknown: 'inconnu',
        },
      },
      panel: {
        noselection: {
          title: 'Aucun panneau sélectionné',
          desc: 'Cliquez sur Gérer les panneaux pour ajouter des panneaux à votre tableau de bord.',
        },
        lives: {
          name: 'Durées de vie',
          desc: 'Pièces produites par outil, triées par performance.',
        },
        anomalies: {
          name: 'Anomalies',
          desc: 'Outils avec un nombre de pièces significativement inférieur à la moyenne.',
        },
        machine: {
          name: 'Par machine',
          desc: 'Moyenne des pièces produites par machine.',
        },
        operator: {
          name: 'Par opérateur',
          desc: 'Moyenne des pièces produites par opérateur.',
        },
        material: {
          name: 'Par matériau',
          desc: 'Moyenne des pièces produites par matériau.',
        },
        vc: {
          name: 'Vitesse de coupe',
          desc: 'Dispersion durée de vie vs vitesse de coupe (Vc).',
        },
        fz: {
          name: "Avance par dent",
          desc: 'Dispersion durée de vie vs avance par dent (fz).',
        },
        ap: {
          name: 'Profondeur de passe',
          desc: 'Dispersion durée de vie vs profondeur de passe axiale (ap).',
        },
      },
      drawer: {
        title: 'Gérer les panneaux',
        group: {
          overview: 'Vue d\'ensemble',
          context: 'Répartition contextuelle',
          params: 'Analyse des paramètres',
        },
      },
      lives: {
        columns: {
          tool: 'Outil',
          pieces: 'Pièces produites',
          machine: 'Machine',
        },
        legend: {
          new: 'Neuf',
          active: 'Actif',
          worn: 'Usé',
          broken: 'Cassé',
        },
        unit: {
          pieces: 'pcs',
        },
      },
      anomalies: {
        col: {
          tool: 'Outil',
          pieces: 'Pièces',
          vsAvg: 'vs moy.',
          machine: 'Machine',
          operator: 'Opérateur',
          material: 'Matériau',
          vc: 'Vc',
          status: 'Statut',
        },
        badge: {
          broken: 'Cassé',
          investigate: 'À vérifier',
        },
      },
      scatter: {
        nodata: 'Aucune donnée disponible pour cet axe.',
        pieces: 'Pièces',
        axisLabels: {
          vc: 'Vitesse de coupe (Vc)',
          fz: 'Avance par dent (fz)',
          ap: 'Profondeur de passe (ap)',
        },
        note: {
          vc: 'Une Vc élevée (>200 m/min) peut réduire la durée de vie. Zone optimale mise en évidence.',
          fz: 'L\'avance par dent optimale équilibre productivité et usure.',
          ap: 'Des passes profondes augmentent la charge — surveiller la durée de vie.',
        },
      },
      grouped: {
        columns: {
          machine: 'Machine',
          operator: 'Opérateur',
          material: 'Matière',
          avgPieces: 'Pièces moy. / outil',
          tools: 'Outils',
        },
        note: {
          machine: '{{top}} produit {{ratio}}× plus de pièces que {{bottom}}.',
          operator: '{{top}} produit {{ratio}}× plus de pièces que {{bottom}}.',
          material: '{{top}} permet {{ratio}}× plus de pièces que {{bottom}}.',
        },
      },
      kpi: {
        avgLife: 'Durée de vie moy.',
        avgLifeSub: 'max : {{best}} · min : {{worst}}',
        totalPieces: 'Pièces totales',
        totalPiecesSub: 'sur {{n}} outils',
        totalTime: 'Temps d\'usinage total',
        totalTimeSub: 'moy. {{n}} min / outil',
        avgTimePerPiece: 'Temps moy. / pièce',
        totalVolume: 'Volume usiné',
        totalVolumeSub: 'moy. {{n}} cm³ / outil',
        avgMalround: 'Malrond moy.',
        avgMalroundRange: 'plage {{min}} – {{max}}',
        unit: {
          piecesPerTool: 'pièces / outil',
          pieces: 'pièces',
          minutes: 'minutes',
          secPerPiece: 's / pièce',
          cm3: 'cm³ total',
          um: 'µm',
        },
      },
    },
    classic: {
      backLink: '← Nouvelle vue analytique',
    },
    comparison: {
      breadcrumb: {
        analytics: 'Analyses avancées',
        comparison: 'Comparaison de références',
      },
      selector: {
        title: 'Sélection des références',
        description:
          "Choisissez une référence de base et comparez-la avec d'autres pour analyser les performances.",
        baseReference: {
          label: 'Référence de base',
          badge: 'Base',
          placeholder: 'Sélectionner une référence de base...',
          helper: 'La référence de base servira de point de comparaison pour toutes les autres.',
        },
        comparisonReferences: {
          label: 'Références à comparer',
          count: '{{selected}}/{{total}} sélectionnée(s)',
          helper: 'Cliquez sur les références pour les ajouter ou les retirer de la comparaison.',
        },
        summary: {
          title: '{{count}} référence(s) sélectionnée(s)',
        },
        empty: 'Aucune référence disponible pour la comparaison.',
      },
      layout: {
        pill: 'Comparaison avancée',
        title: 'Comparer les performances des références',
        subtitle:
          'Analysez les indicateurs clés pour comprendre l’impact de chaque référence sur la production et la qualité.',
        controls: {
          primaryLabel: 'Référence principale',
          baselineBadge: 'Référence de base',
          comparisonLabel: 'Références à comparer',
          comparisonCount: '{{selected}}/{{total}} sélectionnée(s)',
          baselineIndicator: '(base)',
          selectPlaceholder: 'Sélectionner une référence...',
          multiHelper:
            'Sélectionnez au moins une référence à comparer. La première de la liste fait office de référence principale.',
          refresh: 'Actualiser la comparaison',
          refreshing: 'Actualisation…',
        },
        filters: {
          title: 'Filtres',
          machine: {
            label: 'Machine',
            placeholder: 'Toutes les machines',
            all: 'Toutes les machines',
          },
          lubrication: {
            label: 'Lubrification',
            placeholder: 'Toutes les lubrifications',
            all: 'Toutes les lubrifications',
          },
          material: {
            label: 'Matériau',
            placeholder: 'Tous les matériaux',
            all: 'Tous les matériaux',
          },
          operation: {
            label: 'Opération',
            placeholder: 'Toutes les opérations',
            all: 'Toutes les opérations',
          },
          clear: 'Réinitialiser',
          clearAll: 'Réinitialiser les filtres',
        },
        table: {
          title: 'Comparaison des métriques',
          subtitle: '{{count}} référence(s) comparée(s)',
          metric: 'Métrique',
          baseline: 'Référence de base',
          totalDuration: 'Durée totale moyenne',
          totalDurationHelper: 'Durée moyenne de cycle par outil',
          numberOfParts: 'Nombre de pièces',
          numberOfPartsHelper: 'Total de pièces produites',
          volumeRemoved: 'Volume usiné',
          volumeRemovedHelper: 'Volume total de matière enlevée',
          distanceTravelled: 'Distance parcourue',
          distanceTravelledHelper: "Distance totale parcourue par l'outil",
          malround: 'Malrond moyen',
          malroundHelper: 'Erreur de circularité moyenne',
        },
        metrics: {
          referenceTitle: 'Production — {{reference}}',
          piecesValue: '{{count}} pièce(s)',
          traceHelper: '{{count}} relevé(s) analysé(s)',
          averageCycle: {
            title: 'Durée de cycle moyenne — {{reference}}',
            helper: 'Durée moyenne par relevé (en minutes).',
            value: '{{value}} min',
          },
          cycleEfficiency: {
            title: 'Efficacité des cycles — {{reference}}',
            helper: "Part d'usinage productif dans la durée totale.",
            value: '{{value}} %',
          },
          downtime: {
            title: "Taux d'arrêts — {{reference}}",
            helper: 'Incidents normalisés pour dix relevés récents.',
            value: '{{value}} / 10 relevés',
          },
          operatorCoverage: {
            title: 'Couverture opérateurs — {{reference}}',
            helper: "Opérateur dominant et nombre total d'intervenants.",
            empty: 'Aucune donnée opérateur disponible.',
            valueWithShare: '{{count}} opérateur(s). Leader : {{operator}} ({{share}}%).',
            valueWithoutShare: '{{count}} opérateur(s). Leader : {{operator}}.',
            valueWithoutLeader: '{{count}} opérateur(s) enregistré(s).',
          },
          totalVolume: {
            title: 'Volume usiné total — {{reference}}',
            helper: 'Volume cumulé sur tous les relevés.',
            value: '{{value}} cm³',
          },
          averageVolume: {
            title: 'Volume moyen — {{reference}}',
            helper: "Volume moyen par cycle d'usinage.",
            value: '{{value}} cm³',
          },
          totalDistance: {
            title: 'Distance parcourue totale — {{reference}}',
            helper: 'Distance cumulée sur tous les relevés.',
            value: '{{value}} mm',
          },
          averageDistance: {
            title: 'Distance moyenne — {{reference}}',
            helper: "Distance moyenne par cycle d'usinage.",
            value: '{{value}} mm',
          },
        },
        kpi: {
          sectionTitle: 'Comparaison des KPIs',
          sectionTitleFallback: 'Métriques par Référence',
          labels: {
            baseline: 'Référence',
            comparison: 'Comparaison',
          },
          cycleDuration: {
            title: 'Durée de Cycle Moyenne',
            helper: 'Temps moyen par cycle de production',
          },
          cycleEfficiency: {
            title: 'Efficacité du Cycle',
            helper: "Rapport entre temps d'usinage effectif et temps total",
          },
          downtime: {
            title: "Temps d'Arrêt par 10 Traces",
            helper: "Nombre d'incidents moyens pour 10 traces",
          },
          malround: {
            title: 'Malrond Moyen',
            helper: 'Erreur de circularité moyenne',
          },
          pieces: {
            title: 'Pièces Produites',
            helper: 'Nombre total de pièces usinées',
          },
        },
        chartTitles: {
          productionTrend: 'Tendance de production comparée',
          cycleDistribution: 'Distribution des durées de cycle',
          machineContribution: 'Contribution des machines',
          operatorMix: 'Répartition des opérateurs',
        },
        charts: {
          common: {
            minutes: '{{value}} min',
            pieces: '{{value}} pièce(s)',
          },
          legend: {
            showMore: 'Afficher {{count}} de plus',
            showLess: 'Afficher moins',
            topItems: 'Top {{count}} éléments',
            allItems: 'Tous les {{count}} éléments',
          },
          trend: {
            description:
              'Suivez les volumes productifs et les arrêts pour chaque référence sélectionnée.',
            legendProduced: 'Minutes productives — {{reference}}',
            legendDowntime: "Minutes d'arrêt — {{reference}}",
            empty: 'Aucune tendance exploitable pour cette sélection.',
          },
          cycle: {
            description:
              'Analysez la répartition des durées de cycle pour identifier les écarts entre références.',
            tabLabel: 'Référence {{reference}}',
            xLabel: 'Durée de cycle (min)',
            yLabel: 'Nombre de relevés',
            empty: 'Aucune durée enregistrée pour cette référence.',
            emptyAll: 'Aucune durée de cycle exploitable pour la sélection.',
          },
          machine: {
            description:
              'Comparez la contribution en pièces de chaque machine selon les références actives.',
            legend: 'Machine — {{reference}}',
            empty: 'Aucune activité machine exploitable pour cette sélection.',
            unknown: 'Machine non renseignée',
          },
          operator: {
            description:
              'Identifiez les opérateurs les plus impliqués pour chaque référence comparée.',
            tabLabel: 'Référence {{reference}}',
            empty: 'Aucune contribution opérateur disponible.',
            unknown: 'Opérateur non renseigné',
          },
        },
        trend: {
          pieces: 'Pièces',
          cycleTime: 'Durée de cycle',
          piecesLabel: 'Nombre de pièces',
          cycleTimeLabel: 'Durée moyenne (min)',
          noData: 'Aucune donnée de tendance disponible pour cette période.',
        },
        distribution: {
          mean: 'Moyenne',
          meanLabel: 'Moy. {{reference}}',
          traces: 'relevés',
        },
        contribution: {
          piecesValue: '{{value}} pièce(s)',
          noData: 'Aucune donnée de contribution disponible.',
        },
        qualityDowntime: {
          malround: 'Malrond {{reference}}',
          downtime: "Temps d'arrêt {{reference}}",
          malroundLabel: 'Malrond moyen',
          downtimeLabel: 'Arrêts / 10 relevés',
          threshold: 'Seuil',
          noValue: 'N/A',
          noData: "Aucune donnée de qualité ou temps d'arrêt disponible.",
        },
        summary: {
          title: 'Synthèse des références comparées',
          description:
            '{{count}} référence(s), {{totalTraces}} relevé(s) et {{totalPieces}} pièce(s) couverts par la sélection.',
        },
        state: {
          loading: 'Chargement des données de comparaison…',
          empty: 'Aucune donnée de comparaison exploitable pour cette sélection.',
          emptyShort: 'Aucune mesure disponible pour cette sélection.',
          noReferences: "Aucune référence active trouvée pour l'instant.",
          errorTitle: 'Impossible de charger la comparaison',
          errorDescription:
            'La récupération des données a échoué. Des données illustratives sont affichées en attendant une nouvelle tentative.',
          usingSample:
            'Des données simulées sont affichées pour conserver la visibilité. Rafraîchissez la page pour relancer la synchronisation.',
          errorDetailsTitle: 'Détails techniques',
          errorDetailMessage: 'Message : {{value}}',
          errorDetailCode: 'Code : {{value}}',
          errorDetailName: 'Type : {{value}}',
          errorDetailTimestamp: 'Horodatage : {{value}}',
        },
      },
      performance: {
        title: 'Métriques de Performance par Référence',
        description:
          'Comparez les métriques moyennes par outil entre les références sélectionnées.',
        meanDuration: {
          title: 'Durée Totale Moyenne',
          yAxisLabel: 'Durée (min)',
          tooltip: 'Durée Moyenne',
        },
        meanPieces: {
          title: 'Nombre de Pièces Moyen',
          yAxisLabel: 'Pièces',
          tooltip: 'Pièces Moyennes',
        },
        meanVolume: {
          title: 'Volume Retiré Moyen',
          yAxisLabel: 'Volume (cm³)',
          tooltip: 'Volume Moyen',
        },
        meanMalround: {
          title: 'Malrond Moyen',
          yAxisLabel: 'Malrond (µm)',
          tooltip: 'Malrond Moyen',
        },
      },
      distribution: {
        title: 'Distribution de Performance des Outils',
        description:
          'Analysez la distribution des métriques de performance des outils entre les références sélectionnées.',
        duration: {
          title: 'Distribution des Durées',
          yAxisLabel: 'Durée',
        },
        pieces: {
          title: 'Distribution du Nombre de Pièces',
          yAxisLabel: 'Pièces',
        },
        volume: {
          title: 'Distribution du Volume Retiré',
          yAxisLabel: 'Volume',
        },
        malround: {
          title: 'Distribution du Malround',
          yAxisLabel: 'Malround (µm)',
        },
      },
    },
    charts: {
      legend: {
        showMore: 'Afficher {{count}} de plus',
        showLess: 'Afficher moins',
        topItems: 'Top {{count}} éléments',
        allItems: 'Tous les {{count}} éléments',
      },
      errors: {
        invalidConfig: 'Configuration de graphique invalide',
        noData: 'Aucune donnée disponible',
        unknownType: 'Type de graphique inconnu',
      },
    },
    chartError: {
      title: 'Erreur de rendu du graphique',
      unexpected: "Une erreur inattendue s'est produite",
      tryAgain: 'Réessayer',
      stackTrace: "Pile d'appels",
    },
  },
};
